import { LinkIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/core/Button';
import { ButtonPanel } from '@/components/core/ButtonPanel';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import type { TaskFormApi } from '@/components/task-view/TaskInteractiveDetails';
import { type EditorTask, showsRelatedItemBrowse } from '@/components/task-view/taskView';
import { useTask } from '@/components/task-view/taskView.queries';
import type { TaskWorkflowApi } from '@/components/task-view/TaskViewActions';
import { TaskViewInEditor } from '@/components/task-view/TaskViewInEditor';
import ConfirmationModal from '@/dialogs/confirmation-modals/ConfirmationModal';
import { useTranslation } from '@/hooks/useTranslation';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';

type TaskViewDialogProps = {
    task: EditorTask;
    onClose: () => void;
    onOpenRelatedItem?: (task: EditorTask) => void;
    onBrowse?: (search: Record<string, string>) => void;
    onTaskChanged?: () => void;
};

const TaskViewDialog = ({
    task: initialTask,
    onClose,
    onOpenRelatedItem,
    onBrowse,
    onTaskChanged,
}: TaskViewDialogProps) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(true);
    const [task, setTask] = useState(initialTask);
    const [discardOpen, setDiscardOpen] = useState(false);
    const closed = useRef(false);
    const closeRequested = useRef(false);
    const pendingClose = useRef(false);
    const formApi = useRef<TaskFormApi | null>(null);
    const workflowApi = useRef<TaskWorkflowApi | null>(null);
    const [formSaving, setFormSaving] = useState(false);
    const [formEditable, setFormEditable] = useState(false);
    const [formDirty, setFormDirty] = useState(false);
    const [canCloseTask, setCanCloseTask] = useState(false);
    const [canReopenTask, setCanReopenTask] = useState(false);
    const [closingTask, setClosingTask] = useState(false);
    const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
    const onFormBind = useCallback((api: TaskFormApi) => {
        formApi.current = api;
        setFormSaving(api.saving);
        setFormEditable(api.editable);
        setFormDirty(api.dirty);
    }, []);
    const onWorkflowBind = useCallback((api: TaskWorkflowApi) => {
        workflowApi.current = api;
        setCanCloseTask(api.canClose);
        setCanReopenTask(api.canReopen);
        setClosingTask(api.closing);
    }, []);
    const { data: loadedTask } = useTask({
        variables: { taskId: initialTask.id },
        enabled: initialTask.id != null && initialTask.id !== '',
    });

    useEffect(() => {
        if (loadedTask) {
            setTask(loadedTask);
        }
    }, [loadedTask]);

    useEffect(() => {
        try {
            const root = getRootScope(getInjector());
            if (typeof root?.$on !== 'function') {
                return undefined;
            }
            const unsubscribe = root.$on('taskViewUpdated', (_event: unknown, saved: unknown) => {
                if (!saved || typeof saved !== 'object') {
                    return;
                }
                const updated = saved as EditorTask;
                if (String(updated.id) !== String(initialTask.id)) {
                    return;
                }
                queryClient.setQueryData(['task', { taskId: updated.id }], updated);
                void queryClient.invalidateQueries({ queryKey: ['taskComments'] });
                setTask(updated);
                onTaskChanged?.();
            });
            return () => {
                unsubscribe();
            };
        } catch {
            // Hosts without Angular, including unit tests, have no root scope.
            return undefined;
        }
    }, [initialTask.id, onTaskChanged, queryClient]);

    const openOneItem = task.items?.length === 1;
    const showRelatedItems = showsRelatedItemBrowse(task);

    const close = useCallback(() => {
        if (closed.current) {
            return;
        }
        closed.current = true;
        closeRequested.current = true;
        setOpen(false);
        onClose();
    }, [onClose]);

    const requestClose = (event?: { preventDefault: () => void }) => {
        event?.preventDefault();
        if (formApi.current?.dirty) {
            setDiscardOpen(true);
            return;
        }
        close();
    };

    const discardChanges = () => {
        pendingClose.current = true;
        setDiscardOpen(false);
    };

    const closeTaskPatch = () => (formApi.current?.dirty ? formApi.current.draft() : {});

    const requestCloseTask = () => {
        if (workflowApi.current?.validateClose(closeTaskPatch) === false) {
            return;
        }
        setCloseConfirmOpen(true);
    };

    const confirmCloseTask = () => {
        setCloseConfirmOpen(false);
        workflowApi.current?.closeTask(closeTaskPatch);
    };

    useEffect(() => {
        if (!discardOpen && pendingClose.current) {
            pendingClose.current = false;
            close();
        }
    }, [close, discardOpen]);

    return (
        <>
            <Dialog
                open={open}
                onOpenChange={(next) => {
                    if (!next && closeRequested.current) {
                        close();
                    }
                }}
            >
                <DialogContent
                    variant="large"
                    onEscapeKeyDown={(event) => event.preventDefault()}
                    onInteractOutside={(event) => event.preventDefault()}
                    onPointerDownOutside={(event) => event.preventDefault()}
                    onFocusOutside={(event) => event.preventDefault()}
                >
                    <DialogHeader>
                        <DialogHeaderDefault
                            closeLabel={t('CLOSE')}
                            onClose={requestClose}
                            actions={(openOneItem || showRelatedItems) ? (
                                <>
                                    {openOneItem && (
                                        <ButtonPanel
                                            id="open-related-item"
                                            isDisabled={false}
                                            icon={PaperClipIcon}
                                            tooltip={t('TASK.OPEN_RELATED_ITEM')}
                                            aria-label={t('TASK.OPEN_RELATED_ITEM')}
                                            onClick={() => onOpenRelatedItem?.(task)}
                                        />
                                    )}
                                    {showRelatedItems && (
                                        <ButtonPanel
                                            id="show-related-items"
                                            isDisabled={false}
                                            icon={LinkIcon}
                                            tooltip={t('TASK.SHOW_RELATED_ITEMS')}
                                            aria-label={t('TASK.SHOW_RELATED_ITEMS')}
                                            onClick={() => onBrowse?.({ taskId: String(task.id) })}
                                        />
                                    )}
                                </>
                            ) : undefined}
                        >
                            {task.title}
                        </DialogHeaderDefault>
                    </DialogHeader>
                    <DialogBody scrollable className="tw-max-h-[70vh]">
                        <TaskViewInEditor
                            task={task}
                            showTitle={false}
                            interactive
                            onTaskUpdated={(saved) => {
                                setTask(saved);
                                onTaskChanged?.();
                            }}
                            onFormBind={onFormBind}
                            onWorkflowBind={onWorkflowBind}
                            onBrowse={onBrowse}
                        />
                    </DialogBody>
                    <DialogFooter>
                        <div className="tw-flex tw-w-full tw-flex-col-reverse tw-gap-2 sm:tw-w-auto sm:tw-flex-row">
                            {formEditable && (
                                <Button
                                    id="save-task"
                                    loading={formSaving}
                                    disabled={!formDirty || formSaving}
                                    onClick={() => formApi.current?.save()}
                                >
                                    {t('SAVE')}
                                </Button>
                            )}
                            {canCloseTask && (
                                <Button
                                    id="close-task"
                                    variant="destructive"
                                    loading={closingTask}
                                    onClick={requestCloseTask}
                                >
                                    {t('TASK.CLOSE_TASK')}
                                </Button>
                            )}
                            {canReopenTask && (
                                <Button
                                    id="reopen-task"
                                    variant="outline"
                                    loading={closingTask}
                                    onClick={() => workflowApi.current?.reopenTask()}
                                >
                                    {t('REOPEN_TASK')}
                                </Button>
                            )}
                            <Button id="close-task-dialog" variant="secondary" onClick={() => requestClose()}>
                                {t('CLOSE')}
                            </Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <ConfirmationModal
                title="MODAL.CONFIRM_HEADER"
                subTitle="MODAL.DISMISS_WITH_UNSAVED_CHANGES"
                show={discardOpen}
                preventDismissOutside
                onSubmit={discardChanges}
                onClose={() => setDiscardOpen(false)}
            />
            <ConfirmationModal
                title="MODAL.CONFIRM_HEADER"
                subTitle="TASK.CLOSE_TASK.CONFIRM"
                discardBtnText="TASK.CLOSE_TASK"
                closeBtnText="CANCEL"
                btnVariant="destructive"
                show={closeConfirmOpen}
                preventDismissOutside
                onSubmit={confirmCloseTask}
                onClose={() => setCloseConfirmOpen(false)}
            />
        </>
    );
};

export { TaskViewDialog };
export type { TaskViewDialogProps };
